// components/SalesInventoryReports.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import Table from '../../shared/components/layout/Table';
import Button from '../../shared/components/ui/Button';
import PaginationComponent from '../../shared/components/PaginationComponent';
import NoDataFound from '../components/layout/NoDataFound';

// <-- The APIs
import {
  getInventoryReports // <-- your API to fetch the inventory data
} from '../services/api/implementation/impl';

// <-- The same utility used in UsersList
import { transformData } from '../../super-admin/utils/transformData';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useProjectsList } from '../../../contexts/ProjectsContext';

const SalesInventoryReports = ({
  selectedProjectId = null,
  onSelectedProjectIdChange
}) => {
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  /*
   * -------------------------------------------------
   * Data Format for the Inventory Table
   * (No Sales Contribution column)
   * -------------------------------------------------
   */
  const inventoryDataFormat = [
    'id',
    'accommodation_name',
    'accommodation_type',
    'plot_area',
    'units_sold',
    'units_available',
    'units_sold_contribution'
  ];

  /*
   * -------------------------------------------------
   * State & Refs
   * -------------------------------------------------
   */
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('Company');
  const applyFilterRef = useRef(null);
  const [checkedItems, setCheckedItems] = useState([]);

  // For server-side pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // or make this dynamic

  // react-hook-form
  const { register } = useForm();

  /*
   * -------------------------------------------------
   * Fetch Projects
   * -------------------------------------------------
   */

  /*
   * -------------------------------------------------
   * Fetch Inventory Reports (Server-Side Pagination)
   * -------------------------------------------------
   */
  const { data: inventoryResponse, isFetching: isFetchingInventory } = useQuery({
    queryKey: ['inventoryReports', page, limit, selectedProjectId],
    queryFn: () => getInventoryReports(page, limit, selectedProjectId),
    refetchOnMount: 'always',
    keepPreviousData: true
  });

  // inventoryResponse is assumed to have the form { data: [...], meta: { total, limit, page } }
  const inventoryData = inventoryResponse?.data || [];

  // total number of items returned by the server
  const totalItems = inventoryResponse?.meta?.total || 0;
  // compute total pages
  const totalPages = Math.ceil(totalItems / limit);

  /*
   * -------------------------------------------------
   * Transform the Inventory Data
   * -------------------------------------------------
   */
  const processedInventory = inventoryData.map((item) => {
    // Example fields. Adapt to your real API data.
    // Removing "salesContribution" entirely.
    // 'units_sold_contribution' can be turned into a percentage string if desired.
    const unitsSoldContribution = item?.unitSoldContribution ?? 0; // Ensure correct field name
    return {
      id: item?.id ?? 'no-id', // unique identifier
      accommodation_name: item?.name || 'N/A',
      accommodation_type: item?.accomodationType || 'N/A',
      plot_area: item?.plotArea || 'N/A',
      units_sold: item?.unitsSold || 0,
      units_available: item?.unitsAvailable || 0,
      units_sold_contribution: `${unitsSoldContribution}%`
    };
  });

  // Apply transformData
  const transformedInventoryData = processedInventory.map((row) =>
    transformData(row, inventoryDataFormat)
  );

  /*
   * -------------------------------------------------
   * Filter Widget Logic
   * -------------------------------------------------
   */
  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
    handleClearAll();
  };

  const handleFilterClick = (filterName) => {
    setSelectedFilter(filterName);
  };

  const handleCheckboxChange = (val) => {
    setCheckedItems((prev) =>
      prev.includes(val) ? prev.filter((item) => item !== val) : [...prev, val]
    );
  };

  const handleClearAll = () => {
    setCheckedItems([]);
    setSelectedFilter('Company');
  };

  // Close the filter widget if clicked outside

  // Track page view
  useEffect(() => {
    mixpanel.track('Sales Inventory Report Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (applyFilterRef.current && !applyFilterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
        handleClearAll();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /*
   * -------------------------------------------------
   * Page Change
   * -------------------------------------------------
   */
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  /*
   * -------------------------------------------------
   * Project Selection
   * -------------------------------------------------
   */
  const handleProjectNameChange = (e) => {
    const v = e.target.value;
    onSelectedProjectIdChange?.(v === '' ? null : v);
    setPage(1); // Reset to first page when project changes
  };

  /*
   * -------------------------------------------------
   * Render
   * -------------------------------------------------
   */
  return (
    <div className="z-5 flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-gray-100">
      {/* Filter Widget */}
      {isFilterOpen && (
        <div
          ref={applyFilterRef}
          className="absolute right-[2rem] mt-[14.5rem] w-[33rem] bg-white border rounded-lg shadow-lg z-[1000]">
          <div className="py-3 px-4 border-b flex justify-between items-center">
            <h4 className="font-semibold">Apply Filters</h4>
            <p
              onClick={handleClearAll}
              className="text-blue-500 underline hover:text-blue-700 transition duration-200 cursor-pointer">
              Clear all
            </p>
          </div>
          <div className="flex justify-between items-start">
            {/* Left Pane (tabs) */}
            <div className="flex flex-col w-[30%]">
              <p
                className={`py-3 px-4 cursor-pointer border-b ${selectedFilter === 'Company' ? 'bg-[#d0edf1]' : ''
                  }`}
                onClick={() => handleFilterClick('Company')}>
                Company
              </p>
              <p
                className={`py-3 px-4 cursor-pointer border-b ${selectedFilter === 'Project Name' ? 'bg-[#d0edf1]' : ''
                  }`}
                onClick={() => handleFilterClick('Project Name')}>
                Project Name
              </p>
              <p
                className={`py-3 px-4 cursor-pointer ${selectedFilter === 'Month' ? 'bg-[#d0edf1]' : ''
                  }`}
                onClick={() => handleFilterClick('Month')}>
                Month
              </p>
              <p
                className={`py-3 px-4 cursor-pointer border-t ${selectedFilter === 'Block' ? 'bg-[#d0edf1]' : ''
                  }`}
                onClick={() => handleFilterClick('Block')}>
                Block
              </p>
            </div>
            {/* Right Pane (checkboxes) */}
            <div className="flex flex-col w-[70%] border-l">
              {selectedFilter === 'Company' && (
                <>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(1)}
                      onChange={() => handleCheckboxChange(1)}
                    />
                    Ved Builders
                  </div>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(2)}
                      onChange={() => handleCheckboxChange(2)}
                    />
                    Nupur Constructions
                  </div>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(9)}
                      onChange={() => handleCheckboxChange(9)}
                    />
                    LR Constructions
                  </div>
                </>
              )}
              {selectedFilter === 'Project Name' && (
                <>
                  {/* Dynamically generate project names based on fetched projects */}
                  {projects?.map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={checkedItems.includes(project.name)}
                        onChange={() => handleCheckboxChange(project.name)}
                      />
                      {project.name}
                    </div>
                  ))}
                </>
              )}
              {selectedFilter === 'Month' && (
                <div className="grid grid-cols-3 gap-1">
                  {[
                    'January',
                    'February',
                    'March',
                    'April',
                    'May',
                    'June',
                    'July',
                    'August',
                    'September',
                    'October',
                    'November',
                    'December'
                  ].map((month) => (
                    <div key={month} className="flex items-center gap-2 py-2 px-2 text-[#67676C]">
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={checkedItems.includes(month)}
                        onChange={() => handleCheckboxChange(month)}
                      />
                      {month}
                    </div>
                  ))}
                </div>
              )}
              {selectedFilter === 'Block' && (
                <>
                  {/* Dynamically generate block names based on fetched inventory data */}
                  {inventoryData.map((item) => (
                    <div key={item.id} className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={checkedItems.includes(item.name)}
                        onChange={() => handleCheckboxChange(item.name)}
                      />
                      {item.name}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-4 p-6 border-t">
            <Button displayType="secondary" onClick={toggleFilter}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                /* Implement Apply Filter Logic */
              }}>
              Apply
            </Button>
          </div>
        </div>
      )}

      {/* Table — flex fills space below tabs (Sales Reports shell) */}
      <div className="flex min-h-0 w-full flex-1 flex-col">
        {transformedInventoryData.length > 0 ? (
          <div className="mx-6 mb-4 mt-4 flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 flex-col gap-2">
              <div className="relative h-full min-h-0 flex-1 overflow-hidden">
                <Table
                  headers={[
                    'Accommodation Name',
                    'Accommodation Type',
                    'Plot Area',
                    'No of Units Sold',
                    'Units Available',
                    'Units Sold Contribution'
                  ]}
                  data={transformedInventoryData}
                  identifier={'id'}
                  isViewEnable={false}
                  isDeleteEnable={false}
                  isEditEnable={false}
                  isWrap={true}
                />
              </div>
              <div className="relative flex shrink-0 justify-center py-2">
                <PaginationComponent
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  isFetching={isFetchingInventory}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="pt-[3rem]">
            <NoDataFound disabledBtn screenHeight={true} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesInventoryReports;
